<template>
  <div :class="$style.passwordContainer">
    <!-- TODO: Remove v$.password.$error && $style.error and
      use :invalid when it is fixed at excalibur #363 -->
    <XFormField
      :class="[$style.inputGroup, v$.password.$error && $style.error]"
      :label="label"
      data-testid="passwordInputGroup"
      :validations="v$.password"
      :message="basicRequirements"
      full-width
      grouped
    >
      <!-- TODO: Change trigger showFullRequirements
        to focus when it is fixed on excalibur #363 -->
      <XTextInput
        v-model="inputData.password"
        :autofocus="autofocus"
        :class="$style.input"
        :type="activeType.name"
        :placeholder="placeholder"
        data-testid="passwordField"
        full-width
        @input="handleValidation(), showFullRequirements()"
        @click="showFullRequirements()"
        @blur="handleFullRequirements()"
      />
      <XButton
        model="plain"
        data-testid="showHidePassword"
        @click="changePasswordType()"
      >
        <XIcon
          :icon="activeType.icon"
          fixed-width
        />
      </XButton>
    </XFormField>

    <div
      v-if="showRequirements && isFullRequirementsVisible"
      data-testid="fullRequirements"
    >
      <h5
        id="fullRequirements"
        :class="$style.requirementsTitle"
      >
        {{ $t('auth.passwordFullRequirements.title') }}
      </h5>
      <ul
        aria-labelledby="fullRequirements"
        :class="$style.requirementsList"
      >
        <li
          v-for="requirement in fullRequirements"
          :key="requirement.$message"
          :data-testid="`${requirement.name}Requirement`"
          :class="[
            $style.requirementsListItem,
            requirement.isValid && $style.success,
            requirement.hasError && $style.error,
          ]"
        >
          <XIcon
            :class="[
              $style.requirementsListItemIcon,
              requirement.isValid && $style.success,
              requirement.hasError && $style.error,
            ]"
            :icon="requirement.icon.name"
            :icon-style="requirement.icon.style"
            size="xs"
            fixed-width
          />
          <span :class="$style.requirementsListItemText">
            {{ requirement.$message }}
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { defineComponent, reactive, ref, computed, watch } from 'vue';
import { useI18n } from '@/locales';

// TOGGLE_FORCE_STRONG_PASSWORD_ENABLED
// Remove import { useStore } from '@/store';
import { useStore } from '@/store';

import { PASSWORD } from '@auth/constants';
import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import {
  required,
  minLength,
  maxLength,
  containsUpperAndLowercase,
  containsNumber,
  containsSpecial,
  hasNoSequentialCharacters,
} from '@/modules/base/validators/composition';

function usePasswordVisibility() {
  const passwordTypes = {
    hidden: {
      name: 'password',
      icon: 'eye',
    },
    visible: {
      name: 'text',
      icon: 'eye-slash',
    },
  };
  const activeType = ref(passwordTypes.hidden);

  function changePasswordType() {
    activeType.value = Object.values(passwordTypes).find(
      (type) => type.name !== activeType.value.name
    );
  }

  return {
    activeType,
    changePasswordType,
  };
}

function useFilterForbiddenWords(props) {
  function removeAccents(word) {
    return word.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  function convertStringToArray(string) {
    return string.split(' ');
  }

  function isBigWord(word) {
    return word.length > 2;
  }

  function filterValidWords(words) {
    const defaultWords = convertStringToArray(words.toLowerCase()).filter((word) =>
      isBigWord(word)
    );

    if (!defaultWords?.length) return null;

    const normalizedWords = defaultWords.map((word) => removeAccents(word));

    return [...defaultWords, ...normalizedWords];
  }

  const computedForbiddenWords = computed(() => filterValidWords(props.forbiddenWords));

  return {
    removeAccents,
    computedForbiddenWords,
  };
}

function usePasswordRequirements(props, v$) {
  const outOfInput = ref(false);
  const isFullRequirementsVisible = ref(false);
  const fullRequirementsList = [
    'minLength',
    'containsNumber',
    'upperAndDown',
    'containsSpecial',
    'hasNoSequentialCharacters',
    'cannotContainsName',
  ];
  const fullRequirementsIcons = {
    default: {
      name: 'circle',
      style: 'fas',
    },
    error: {
      name: 'times',
      style: 'fal',
    },
    success: {
      name: 'check',
      style: 'fal',
    },
  };

  function showFullRequirements() {
    if (!isFullRequirementsVisible.value) isFullRequirementsVisible.value = true;
  }

  function getFullRequirementIcon(isValidRequirement) {
    if (outOfInput.value && !isValidRequirement) {
      return fullRequirementsIcons.error;
    }

    if (isValidRequirement) {
      return fullRequirementsIcons.success;
    }

    return fullRequirementsIcons.default;
  }

  const fullRequirements = computed(() =>
    Object.entries(v$.value.password)
      .map((value) => {
        const requirement = value[1];

        if (fullRequirementsList.includes(value[0])) {
          const isValidRequirement = !requirement.$invalid && v$.value.password.$model.length > 0;

          return {
            ...requirement,
            ...{
              name: value[0],
              isValid: isValidRequirement,
              hasError: outOfInput.value && !isValidRequirement,
              icon: getFullRequirementIcon(isValidRequirement),
            },
          };
        }

        return false;
      })
      .filter((value) => value !== false)
  );

  function handleFullRequirements() {
    v$.value.password.$touch();
    outOfInput.value = true;
  }

  const basicRequirements = computed(() =>
    props.isBasicValidation ? v$.value.password?.$errors[0]?.$message : null
  );

  return {
    basicRequirements,
    fullRequirements,
    isFullRequirementsVisible,
    handleFullRequirements,
    showFullRequirements,
  };
}

function usePasswordValidation(props, emit) {
  // TOGGLE_FORCE_STRONG_PASSWORD_ENABLED
  // Remove store
  // Remove toggles
  const store = useStore();
  const toggles = computed(() => store.getters['info/getToggles']);

  const { t } = useI18n();

  const isValidPassword = ref(false);
  const minLengthNumber = PASSWORD.MINLENGTH;
  const maxLengthNumber = PASSWORD.MAXLENGTH;

  const inputData = reactive({
    password: '',
  });

  const { computedForbiddenWords, removeAccents } = useFilterForbiddenWords(props);

  function checkIfPasswordContainsForbiddenWords(password) {
    return computedForbiddenWords.value.find((word) =>
      removeAccents(password.toLowerCase()).includes(word)
    );
  }

  const basicValidators = {
    required,
    minLength: helpers.withParams({ min: minLengthNumber }, minLength(minLengthNumber)),
    maxLength: helpers.withParams({ max: maxLengthNumber }, maxLength(maxLengthNumber)),
  };

  const strongValidators = {
    ...basicValidators,
    ...{
      minLength: helpers.withMessage(
        () =>
          t('auth.passwordFullRequirements.minLength', {
            min: minLengthNumber,
          }),
        minLength(minLengthNumber)
      ),
      containsNumber: helpers.withMessage(
        () => t('auth.passwordFullRequirements.containsNumber'),
        containsNumber
      ),
      upperAndDown: helpers.withMessage(
        () => t('auth.passwordFullRequirements.upperAndDown'),
        containsUpperAndLowercase
      ),
      containsSpecial: helpers.withMessage(
        () => t('auth.passwordFullRequirements.containsSpecial'),
        containsSpecial
      ),
      hasNoSequentialCharacters: helpers.withMessage(
        () => t('auth.passwordFullRequirements.hasNoSequentialCharacters'),
        hasNoSequentialCharacters
      ),
      cannotContainsName: helpers.withMessage(
        () => t('auth.passwordFullRequirements.cannotContainsName'),
        (value) => {
          if (!computedForbiddenWords.value?.length) return true;

          return checkIfPasswordContainsForbiddenWords(value) ? !helpers.req(true) : true;
        }
      ),
    },
  };

  const passwordValidators = computed(() => {
    // TOGGLE_FORCE_STRONG_PASSWORD_ENABLED
    // Remove || !toggles.value.toggleForceStrongPasswordEnabled
    if (props.isBasicValidation || !toggles.value.toggleForceStrongPasswordEnabled) {
      return basicValidators;
    }

    return strongValidators;
  });

  const v$ = useVuelidate(
    {
      password: passwordValidators,
    },
    inputData
  );

  function handleValidation() {
    if (props.isBasicValidation) v$.value.password.$touch();

    isValidPassword.value = !v$.value.password.$invalid && !v$.value.password.$error;

    emit('input', inputData.password, isValidPassword.value, v$.value.password);
  }

  return {
    v$,
    inputData,
    minLengthNumber,
    handleValidation,
  };
}

export default defineComponent({
  name: 'AuthPasswordField',
  props: {
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    forbiddenWords: {
      type: String,
      default: '',
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    showRequirements: {
      type: Boolean,
      default: false,
    },
    isBasicValidation: {
      type: Boolean,
      default: false,
    },
    value: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit }) {
    const { activeType, changePasswordType } = usePasswordVisibility();

    const { v$, inputData, handleValidation } = usePasswordValidation(props, emit);

    const {
      basicRequirements,
      fullRequirements,
      isFullRequirementsVisible,
      handleFullRequirements,
      showFullRequirements,
    } = usePasswordRequirements(props, v$);

    watch(
      () => props.value,
      (password) => {
        if (password !== inputData.password) inputData.password = password;
      }
    );

    return {
      v$,
      activeType,
      inputData,
      basicRequirements,
      fullRequirements,
      isFullRequirementsVisible,
      changePasswordType,
      handleValidation,
      handleFullRequirements,
      showFullRequirements,
    };
  },
});
</script>

<style lang="scss" module>
.passwordContainer {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  width: 100%;
  gap: var(--space-small-xx);

  .inputGroup {
    // TODO: Remove this code when it is fixed at excalibur #363

    &.error {
      [class*='content']:not([class*='hasFocus']) {
        border-color: var(--color-feedback-error-400);
      }
    }
  }

  .requirementsTitle {
    margin: 0px;
    font-family: var(--font-family-body);
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-regular);
    color: var(--color-neutral-800);
  }

  .requirementsList {
    margin: 0px;
    padding: 0px;
    list-style: none;
  }

  .requirementsListItem {
    display: flex;
    align-items: center;
    gap: var(--space-small-xx);
    color: var(--color-neutral-800);

    &.error {
      color: var(--color-feedback-error-400);
    }

    &.success {
      color: var(--color-feedback-success-500);
    }
  }

  .requirementsListItemIcon {
    width: 12px;

    // Custom size to point icon

    &:not(.error):not(.success) {
      font-size: 6px;
    }
  }

  .requirementsListItemText {
    font-size: var(--font-size-small);
    font-weight: var(--title-regular-weight);
  }
}
</style>
